/**
 * OAuth Applications Management
 * Enterprise-grade UI for managing OAuth/OIDC client applications
 */

import { useState, useEffect, useCallback } from 'react';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import StatCard from '../../components/common/StatCard';
import { useToast } from '../../context/ToastContext';
import { auth1Api } from '../../services/api';
import './OAuthApps.css';

const SCOPES = [
  { value: 'openid', label: 'OpenID', description: 'User identifier' },
  { value: 'profile', label: 'Profile', description: 'Name, picture' },
  { value: 'email', label: 'Email', description: 'Email address' },
  { value: 'phone', label: 'Phone', description: 'Phone number' },
  { value: 'offline_access', label: 'Offline Access', description: 'Refresh tokens' },
];

const GRANT_TYPES = [
  { value: 'authorization_code', label: 'Authorization Code', description: 'Standard OAuth flow' },
  { value: 'refresh_token', label: 'Refresh Token', description: 'Token renewal' },
  { value: 'client_credentials', label: 'Client Credentials', description: 'Machine-to-machine' },
];

const OAuthApps = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    redirectUris: [''],
    allowedScopes: ['openid', 'profile', 'email'],
    grantTypes: ['authorization_code', 'refresh_token'],
    requireConsent: true,
    requirePkce: false,
    isConfidential: true,
    tokenLifetimeSeconds: 3600,
    refreshTokenLifetimeSeconds: 2592000,
    logoUrl: '',
    homepageUrl: '',
    privacyPolicyUrl: '',
    termsOfServiceUrl: '',
  });

  const loadApps = useCallback(async () => {
    try {
      setLoading(true);
      const data = await auth1Api.listOAuthApps();
      setApps(data.applications || []);
    } catch (err) {
      showToast('Failed to load OAuth applications', 'error');
      console.error('Failed to load apps:', err);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  const handleCreateApp = async (e) => {
    e.preventDefault();

    // Validate
    if (!formData.name.trim()) {
      showToast('Application name is required', 'error');
      return;
    }

    const validUris = formData.redirectUris.filter(uri => uri.trim());
    if (validUris.length === 0) {
      showToast('At least one redirect URI is required', 'error');
      return;
    }

    try {
      const result = await auth1Api.createOAuthApp({
        ...formData,
        redirectUris: validUris,
      });

      // Show the secret (only time it's visible)
      setNewSecret({
        clientId: result.clientId,
        clientSecret: result.clientSecret,
        warning: result._warning,
      });

      showToast('OAuth application created successfully', 'success');
      setShowCreateModal(false);
      resetForm();
      loadApps();
    } catch (err) {
      showToast(err.message || 'Failed to create application', 'error');
    }
  };

  const handleUpdateApp = async (e) => {
    e.preventDefault();

    if (!selectedApp) return;

    try {
      await auth1Api.updateOAuthApp(selectedApp.id, {
        name: formData.name,
        description: formData.description,
        redirectUris: formData.redirectUris.filter(uri => uri.trim()),
        allowedScopes: formData.allowedScopes,
        grantTypes: formData.grantTypes,
        requireConsent: formData.requireConsent,
        requirePkce: formData.requirePkce,
        tokenLifetimeSeconds: formData.tokenLifetimeSeconds,
        refreshTokenLifetimeSeconds: formData.refreshTokenLifetimeSeconds,
        logoUrl: formData.logoUrl,
        homepageUrl: formData.homepageUrl,
        privacyPolicyUrl: formData.privacyPolicyUrl,
        termsOfServiceUrl: formData.termsOfServiceUrl,
      });

      showToast('Application updated successfully', 'success');
      setSelectedApp(null);
      loadApps();
    } catch (err) {
      showToast(err.message || 'Failed to update application', 'error');
    }
  };

  const handleRotateSecret = async () => {
    if (!showSecretModal) return;

    try {
      const result = await auth1Api.rotateOAuthSecret(showSecretModal.id);

      setNewSecret({
        clientId: result.clientId,
        clientSecret: result.clientSecret,
        warning: result._warning,
      });

      showToast('Client secret rotated successfully', 'success');
      setShowSecretModal(null);
    } catch (err) {
      showToast(err.message || 'Failed to rotate secret', 'error');
    }
  };

  const handleDeleteApp = async () => {
    if (!showDeleteModal) return;

    try {
      await auth1Api.deleteOAuthApp(showDeleteModal.id);
      showToast('Application deleted successfully', 'success');
      setShowDeleteModal(null);
      loadApps();
    } catch (err) {
      showToast(err.message || 'Failed to delete application', 'error');
    }
  };

  const handleRevokeTokens = async (app) => {
    try {
      await auth1Api.revokeOAuthAppTokens(app.id);
      showToast('All tokens revoked successfully', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to revoke tokens', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      redirectUris: [''],
      allowedScopes: ['openid', 'profile', 'email'],
      grantTypes: ['authorization_code', 'refresh_token'],
      requireConsent: true,
      requirePkce: false,
      isConfidential: true,
      tokenLifetimeSeconds: 3600,
      refreshTokenLifetimeSeconds: 2592000,
      logoUrl: '',
      homepageUrl: '',
      privacyPolicyUrl: '',
      termsOfServiceUrl: '',
    });
  };

  const openEditModal = (app) => {
    setFormData({
      name: app.name,
      description: app.description || '',
      redirectUris: app.redirectUris.length > 0 ? app.redirectUris : [''],
      allowedScopes: app.allowedScopes || ['openid', 'profile', 'email'],
      grantTypes: app.grantTypes || ['authorization_code', 'refresh_token'],
      requireConsent: app.requireConsent !== false,
      requirePkce: app.requirePkce || false,
      isConfidential: app.isConfidential !== false,
      tokenLifetimeSeconds: app.tokenLifetimeSeconds || 3600,
      refreshTokenLifetimeSeconds: app.refreshTokenLifetimeSeconds || 2592000,
      logoUrl: app.logoUrl || '',
      homepageUrl: app.homepageUrl || '',
      privacyPolicyUrl: app.privacyPolicyUrl || '',
      termsOfServiceUrl: app.termsOfServiceUrl || '',
    });
    setSelectedApp(app);
  };

  const addRedirectUri = () => {
    setFormData(prev => ({
      ...prev,
      redirectUris: [...prev.redirectUris, ''],
    }));
  };

  const removeRedirectUri = (index) => {
    setFormData(prev => ({
      ...prev,
      redirectUris: prev.redirectUris.filter((_, i) => i !== index),
    }));
  };

  const updateRedirectUri = (index, value) => {
    setFormData(prev => ({
      ...prev,
      redirectUris: prev.redirectUris.map((uri, i) => i === index ? value : uri),
    }));
  };

  const toggleScope = (scope) => {
    setFormData(prev => ({
      ...prev,
      allowedScopes: prev.allowedScopes.includes(scope)
        ? prev.allowedScopes.filter(s => s !== scope)
        : [...prev.allowedScopes, scope],
    }));
  };

  const toggleGrantType = (grantType) => {
    setFormData(prev => ({
      ...prev,
      grantTypes: prev.grantTypes.includes(grantType)
        ? prev.grantTypes.filter(g => g !== grantType)
        : [...prev.grantTypes, grantType],
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard`, 'success');
  };

  const activeApps = apps.filter(a => a.status === 'active').length;

  const stats = [
    { label: 'Total Applications', value: apps.length.toString(), change: '', changeType: 'neutral' },
    { label: 'Active', value: activeApps.toString(), change: activeApps > 0 ? 'Running' : '', changeType: 'positive' },
    { label: 'Authorization Code', value: apps.filter(a => a.grantTypes?.includes('authorization_code')).length.toString(), change: '', changeType: 'neutral' },
    { label: 'PKCE Required', value: apps.filter(a => a.requirePkce).length.toString(), change: 'Secure', changeType: 'positive' },
  ];

  return (
    <div className="oauth-apps-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">OAuth Applications</h1>
          <p className="page-subtitle">Manage applications that use Auth1 for authentication (SSO)</p>
        </div>
        <div className="page-actions">
          <Button variant="secondary" icon="refresh-cw" onClick={loadApps} disabled={loading}>
            Refresh
          </Button>
          <Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
            Register Application
          </Button>
        </div>
      </div>

      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>

      {/* OIDC Discovery Info */}
      <section className="section oidc-info">
        <div className="section-header">
          <h3 className="section-title">OIDC Discovery</h3>
        </div>
        <div className="oidc-endpoints">
          <div className="endpoint-item">
            <span className="endpoint-label">Discovery URL</span>
            <code className="endpoint-value">https://api.auth1.ai/.well-known/openid-configuration</code>
            <button className="copy-btn" onClick={() => copyToClipboard('https://api.auth1.ai/.well-known/openid-configuration', 'Discovery URL')}>
              <Icon name="clipboard" size="sm" />
            </button>
          </div>
          <div className="endpoint-item">
            <span className="endpoint-label">JWKS URL</span>
            <code className="endpoint-value">https://api.auth1.ai/.well-known/jwks.json</code>
            <button className="copy-btn" onClick={() => copyToClipboard('https://api.auth1.ai/.well-known/jwks.json', 'JWKS URL')}>
              <Icon name="clipboard" size="sm" />
            </button>
          </div>
          <div className="endpoint-item">
            <span className="endpoint-label">Authorization Endpoint</span>
            <code className="endpoint-value">https://api.auth1.ai/oauth/authorize</code>
            <button className="copy-btn" onClick={() => copyToClipboard('https://api.auth1.ai/oauth/authorize', 'Authorization Endpoint')}>
              <Icon name="clipboard" size="sm" />
            </button>
          </div>
          <div className="endpoint-item">
            <span className="endpoint-label">Token Endpoint</span>
            <code className="endpoint-value">https://api.auth1.ai/oauth/token</code>
            <button className="copy-btn" onClick={() => copyToClipboard('https://api.auth1.ai/oauth/token', 'Token Endpoint')}>
              <Icon name="clipboard" size="sm" />
            </button>
          </div>
        </div>
      </section>

      {/* Applications List */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Registered Applications</h3>
        </div>

        {loading ? (
          <div className="loading-state">Loading applications...</div>
        ) : apps.length === 0 ? (
          <div className="empty-state">
            <Icon name="shield" size="xl" />
            <h4>No OAuth Applications</h4>
            <p>Register your first OAuth application to enable SSO for external services.</p>
            <Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
              Register Application
            </Button>
          </div>
        ) : (
          <div className="apps-grid">
            {apps.map((app) => (
              <div key={app.id} className={`app-card ${app.status}`}>
                <div className="app-card-header">
                  {app.logoUrl ? (
                    <img src={app.logoUrl} alt={app.name} className="app-logo" />
                  ) : (
                    <div className="app-logo-placeholder">
                      <Icon name="box" size="lg" />
                    </div>
                  )}
                  <div className="app-info">
                    <h4 className="app-name">{app.name}</h4>
                    <span className={`app-status ${app.status}`}>{app.status}</span>
                  </div>
                  <div className="app-actions">
                    <button className="action-btn" title="Edit" onClick={() => openEditModal(app)}>
                      <Icon name="edit" size="sm" />
                    </button>
                    <button className="action-btn" title="Rotate Secret" onClick={() => setShowSecretModal(app)}>
                      <Icon name="refresh-cw" size="sm" />
                    </button>
                    <button className="action-btn danger" title="Delete" onClick={() => setShowDeleteModal(app)}>
                      <Icon name="trash-2" size="sm" />
                    </button>
                  </div>
                </div>

                <div className="app-card-body">
                  {app.description && <p className="app-description">{app.description}</p>}

                  <div className="credential-row">
                    <span className="credential-label">Client ID</span>
                    <code className="credential-value">{app.clientId}</code>
                    <button className="copy-btn" onClick={() => copyToClipboard(app.clientId, 'Client ID')}>
                      <Icon name="clipboard" size="sm" />
                    </button>
                  </div>

                  <div className="app-meta">
                    <div className="meta-item">
                      <Icon name="link" size="sm" />
                      <span>{app.redirectUris?.length || 0} redirect URIs</span>
                    </div>
                    <div className="meta-item">
                      <Icon name="key" size="sm" />
                      <span>{app.allowedScopes?.length || 0} scopes</span>
                    </div>
                    {app.requirePkce && (
                      <div className="meta-item secure">
                        <Icon name="shield" size="sm" />
                        <span>PKCE</span>
                      </div>
                    )}
                  </div>

                  <div className="scopes-list">
                    {app.allowedScopes?.map((scope) => (
                      <span key={scope} className="scope-badge">{scope}</span>
                    ))}
                  </div>
                </div>

                <div className="app-card-footer">
                  <span className="app-created">Created {new Date(app.createdAt).toLocaleDateString()}</span>
                  <button className="text-btn" onClick={() => handleRevokeTokens(app)}>
                    Revoke All Tokens
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Create/Edit Modal */}
      {(showCreateModal || selectedApp) && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setSelectedApp(null); resetForm(); }}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedApp ? 'Edit Application' : 'Register New OAuth Application'}</h3>
              <button className="close-btn" onClick={() => { setShowCreateModal(false); setSelectedApp(null); resetForm(); }}>
                <Icon name="x" />
              </button>
            </div>

            <form onSubmit={selectedApp ? handleUpdateApp : handleCreateApp}>
              <div className="modal-body">
                <div className="form-section">
                  <h4>Basic Information</h4>

                  <div className="form-group">
                    <label>Application Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="My Application"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of your application"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h4>Redirect URIs *</h4>
                  <p className="form-hint">URLs where users will be redirected after authentication. Must use HTTPS (except localhost).</p>

                  {formData.redirectUris.map((uri, index) => (
                    <div key={index} className="uri-input-row">
                      <input
                        type="url"
                        value={uri}
                        onChange={(e) => updateRedirectUri(index, e.target.value)}
                        placeholder="https://myapp.com/auth/callback"
                      />
                      {formData.redirectUris.length > 1 && (
                        <button type="button" className="remove-btn" onClick={() => removeRedirectUri(index)}>
                          <Icon name="x" size="sm" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="add-uri-btn" onClick={addRedirectUri}>
                    <Icon name="plus" size="sm" /> Add Redirect URI
                  </button>
                </div>

                <div className="form-section">
                  <h4>Allowed Scopes</h4>
                  <div className="scopes-grid">
                    {SCOPES.map((scope) => (
                      <label key={scope.value} className={`scope-option ${formData.allowedScopes.includes(scope.value) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={formData.allowedScopes.includes(scope.value)}
                          onChange={() => toggleScope(scope.value)}
                        />
                        <span className="scope-name">{scope.label}</span>
                        <span className="scope-desc">{scope.description}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-section">
                  <h4>Grant Types</h4>
                  <div className="grants-grid">
                    {GRANT_TYPES.map((grant) => (
                      <label key={grant.value} className={`grant-option ${formData.grantTypes.includes(grant.value) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={formData.grantTypes.includes(grant.value)}
                          onChange={() => toggleGrantType(grant.value)}
                        />
                        <span className="grant-name">{grant.label}</span>
                        <span className="grant-desc">{grant.description}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-section">
                  <h4>Security Settings</h4>
                  <div className="settings-grid">
                    <label className="toggle-option">
                      <input
                        type="checkbox"
                        checked={formData.requireConsent}
                        onChange={(e) => setFormData(prev => ({ ...prev, requireConsent: e.target.checked }))}
                      />
                      <span>Require user consent</span>
                    </label>
                    <label className="toggle-option">
                      <input
                        type="checkbox"
                        checked={formData.requirePkce}
                        onChange={(e) => setFormData(prev => ({ ...prev, requirePkce: e.target.checked }))}
                      />
                      <span>Require PKCE (recommended for public clients)</span>
                    </label>
                    <label className="toggle-option">
                      <input
                        type="checkbox"
                        checked={formData.isConfidential}
                        onChange={(e) => setFormData(prev => ({ ...prev, isConfidential: e.target.checked }))}
                      />
                      <span>Confidential client (can keep secret secure)</span>
                    </label>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Token Lifetimes</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Access Token (seconds)</label>
                      <input
                        type="number"
                        value={formData.tokenLifetimeSeconds}
                        onChange={(e) => setFormData(prev => ({ ...prev, tokenLifetimeSeconds: parseInt(e.target.value) || 3600 }))}
                        min={60}
                        max={86400}
                      />
                      <span className="form-hint">{Math.round(formData.tokenLifetimeSeconds / 60)} minutes</span>
                    </div>
                    <div className="form-group">
                      <label>Refresh Token (seconds)</label>
                      <input
                        type="number"
                        value={formData.refreshTokenLifetimeSeconds}
                        onChange={(e) => setFormData(prev => ({ ...prev, refreshTokenLifetimeSeconds: parseInt(e.target.value) || 2592000 }))}
                        min={3600}
                        max={31536000}
                      />
                      <span className="form-hint">{Math.round(formData.refreshTokenLifetimeSeconds / 86400)} days</span>
                    </div>
                  </div>
                </div>

                <div className="form-section collapsible">
                  <h4>Application URLs (Optional)</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Logo URL</label>
                      <input
                        type="url"
                        value={formData.logoUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, logoUrl: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Homepage URL</label>
                      <input
                        type="url"
                        value={formData.homepageUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, homepageUrl: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Privacy Policy URL</label>
                      <input
                        type="url"
                        value={formData.privacyPolicyUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, privacyPolicyUrl: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Terms of Service URL</label>
                      <input
                        type="url"
                        value={formData.termsOfServiceUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, termsOfServiceUrl: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <Button variant="secondary" type="button" onClick={() => { setShowCreateModal(false); setSelectedApp(null); resetForm(); }}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  {selectedApp ? 'Save Changes' : 'Create Application'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Secret Display Modal */}
      {newSecret && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Client Credentials</h3>
            </div>
            <div className="modal-body">
              <div className="alert warning">
                <Icon name="alert-triangle" />
                <span>{newSecret.warning || 'Store these credentials securely. The client secret will not be shown again.'}</span>
              </div>

              <div className="credential-display">
                <div className="credential-item">
                  <label>Client ID</label>
                  <div className="credential-value-row">
                    <code>{newSecret.clientId}</code>
                    <button className="copy-btn" onClick={() => copyToClipboard(newSecret.clientId, 'Client ID')}>
                      <Icon name="clipboard" size="sm" />
                    </button>
                  </div>
                </div>
                <div className="credential-item">
                  <label>Client Secret</label>
                  <div className="credential-value-row">
                    <code className="secret">{newSecret.clientSecret}</code>
                    <button className="copy-btn" onClick={() => copyToClipboard(newSecret.clientSecret, 'Client Secret')}>
                      <Icon name="clipboard" size="sm" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="primary" onClick={() => setNewSecret(null)}>
                I have saved these credentials
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rotate Secret Confirmation Modal */}
      {showSecretModal && (
        <div className="modal-overlay" onClick={() => setShowSecretModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rotate Client Secret</h3>
              <button className="close-btn" onClick={() => setShowSecretModal(null)}>
                <Icon name="x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="alert danger">
                <Icon name="alert-triangle" />
                <div>
                  <strong>Warning:</strong> Rotating the client secret will immediately invalidate the existing secret.
                  Any integrations using the old secret will stop working until updated.
                </div>
              </div>
              <p>Are you sure you want to rotate the secret for <strong>{showSecretModal.name}</strong>?</p>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowSecretModal(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleRotateSecret}>Rotate Secret</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Application</h3>
              <button className="close-btn" onClick={() => setShowDeleteModal(null)}>
                <Icon name="x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="alert danger">
                <Icon name="alert-triangle" />
                <div>
                  <strong>Warning:</strong> This action cannot be undone. All tokens issued to this application will be invalidated.
                </div>
              </div>
              <p>Are you sure you want to delete <strong>{showDeleteModal.name}</strong>?</p>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowDeleteModal(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleDeleteApp}>Delete Application</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OAuthApps;
